import React, { useEffect, useState } from 'react';
import { ArrowUpCircle, ArrowDownCircle, Briefcase, Save } from 'lucide-react';
import { toast } from 'react-toastify';
import useBank from '../../hooks/useBank';
import SummaryApi from '../../common';
import fetchWithAuth from '../../common/fetchWithAuth';

const DailyTransaction = () => {
    const { getBankDetails } = useBank();
    const [bankList, setBankList] = useState([]);

    // --- State for Add Money (Pure Inflow) ---
    const [addMoneyForm, setAddMoneyForm] = useState({
        amount: '',
        name: '',
        mode: 'Cash',
        note: '',
        category: '',
        reference: '',
        bankId: '',
        isVikri: false
    });

    // --- State for Spend Money (Outflow) ---
    const [spendMoneyForm, setSpendMoneyForm] = useState({
        amount: '',
        name: '',
        mode: 'Cash',
        note: '',
        reference: '',
        bankId: ''
    });

    // --- State for Give Udhar (Lending / Outflow) ---
    const [giveUdharForm, setGiveUdharForm] = useState({
        amount: '',
        paidAmount: '', // New field
        name: '',
        mode: 'Cash', // Default to Cash, or can be UPI
        note: '',
        bankId: ''
    });

    // Handlers
    const handleAddMoneyChange = (e) => {
        const { name, value, type, checked } = e.target; // Destructure type and checked
        setAddMoneyForm(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value // Handle checkbox explicitly
        }));
    };

    const handleSpendMoneyChange = (e) => {
        const { name, value } = e.target;
        setSpendMoneyForm(prev => ({ ...prev, [name]: value }));
    };

    const handleGiveUdharChange = (e) => {
        const { name, value } = e.target;
        setGiveUdharForm(prev => ({ ...prev, [name]: value }));
    };

    const handleAddMoneySubmit = async (e) => {
        e.preventDefault();
        // Validation
        if (!addMoneyForm.amount || !addMoneyForm.name || !addMoneyForm.bankId) {
            toast.error("Please fill required fields (Amount, Name, Bank Account)");
            return;
        }

        try {
            const res = await fetchWithAuth(SummaryApi.addDailyTransaction.url, {
                method: SummaryApi.addDailyTransaction.method,
                body: JSON.stringify(addMoneyForm)
            });

            const data = await res.json();

            if (data.message === "Money added successfully") {
                toast.success("Income Added Successfully");
                setAddMoneyForm({
                    amount: '',
                    name: '',
                    mode: 'Cash',
                    note: '',
                    category: '',
                    reference: '',
                    bankId: '',
                    isVikri: false
                });
                fetchBankDetails(); // Refresh Balances
            } else {
                toast.error(data.message || "Failed to add transaction");
            }
        } catch (error) {
            console.error("Error adding transaction:", error);
            toast.error("Something went wrong");
        }
    };

    const handleSpendMoneySubmit = async (e) => {
        e.preventDefault();
        if (!spendMoneyForm.amount || !spendMoneyForm.name || !spendMoneyForm.bankId) {
            toast.error("Please fill required fields (Amount, Name, Bank Account)");
            return;
        }

        // Check for Overspending
        const selectedBank = bankList.find(b => b._id === spendMoneyForm.bankId);
        if (selectedBank) {
            const currentBalance = parseFloat(selectedBank.balance || 0);
            const spendAmount = parseFloat(spendMoneyForm.amount);
            if (spendAmount > currentBalance) {
                toast.error(`Insufficient Balance! Available: ₹${currentBalance.toLocaleString()}`);
                return; // Stop transaction
            }
        }

        try {
            const res = await fetchWithAuth(SummaryApi.spendDailyTransaction.url, {
                method: SummaryApi.spendDailyTransaction.method,
                body: JSON.stringify(spendMoneyForm)
            });
            const data = await res.json();

            if (data.message === "Money Spent Successfully" || data.message === "Money spent successfully" || data.success) { // consistent check
                toast.success("Expense Recorded Successfully");
                setSpendMoneyForm({
                    amount: '',
                    name: '',
                    mode: 'Cash',
                    note: '',
                    reference: '',
                    bankId: ''
                });
                fetchBankDetails(); // Refresh Balances
            } else {
                toast.error(data.message || "Failed to spend money");
            }
        } catch (err) {
            console.log(err.message);
            toast.error(err.message);
        }
    };

    const handleGiveUdharSubmit = async (e) => {
        e.preventDefault();
        if (!giveUdharForm.amount || !giveUdharForm.name || !giveUdharForm.bankId) {
            toast.error("Please fill required fields (Amount, Name, Bank Account)");
            return;
        }

        try {
            const res = await fetchWithAuth(SummaryApi.addUdharTransaction.url, {
                method: SummaryApi.addUdharTransaction.method,
                body: JSON.stringify(giveUdharForm)
            });
            const data = await res.json();

            if (data.message === "Udhar transaction recorded successfully" || data.message === "Udhar transaction added successfully" || data.success) {
                toast.success("Udhar (Lending) Recorded Successfully");
                setGiveUdharForm({
                    amount: '',
                    paidAmount: '',
                    name: '',
                    mode: 'Cash',
                    note: '',
                    bankId: ''
                });
                fetchBankDetails(); // Refresh Balances
            } else {
                toast.error(data.message || "Failed to record Udhar");
            }
        } catch (err) {
            toast.error(err.message || "Something went wrong");
        }
    }

    const fetchBankDetails = async () => {
        try {
            const data = await getBankDetails();
            if (data.success) {
                setBankList(data.data || []);
            } else if (data.banks) {
                setBankList(data.banks);
            } else {
                setBankList([]);
            }
        } catch (err) {
            console.log(err.message);
            toast.error(err.message);
        }
    };

    useEffect(() => {
        fetchBankDetails();
    }, []);

    return (
        <div className="w-full pb-10">
            {/* Header */}
            <div className="mb-6">
                <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
                    <span className="text-4xl">💸</span> Daily Transaction
                </h1>
                <p className="text-gray-500 text-sm mt-1">Manage Cash Flow: Income, Expenses, and Lending</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

                {/* --- 1. ADD MONEY (INCOME) --- */}
                <div className="bg-white rounded-2xl shadow-sm border border-emerald-100 overflow-hidden flex flex-col">
                    <div className="bg-emerald-50/50 p-4 border-b border-emerald-100 flex items-center gap-3">
                        <div className="p-2 bg-emerald-100 text-emerald-600 rounded-lg">
                            <ArrowDownCircle size={24} />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-gray-800">Add Income</h2>
                            <p className="text-xs text-gray-500">Sales, Deposits, Received Money</p>
                        </div>
                    </div>

                    <div className="p-6">
                        <form onSubmit={handleAddMoneySubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Amount (₹)<span className="text-red-500"> *</span></label>
                                <div className="relative">
                                    <span className="absolute left-3 top-2.5 text-gray-400 font-bold">₹</span>
                                    <input min={0} type="number" name="amount" value={addMoneyForm.amount} onChange={handleAddMoneyChange} placeholder="0.00" className="w-full pl-8 pr-4 py-2 rounded-lg border border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all font-mono text-lg font-bold text-emerald-600" required />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Particulars / From Whom <span className="text-red-500"> *</span></label>
                                <input type="text" name="name" value={addMoneyForm.name} onChange={handleAddMoneyChange} placeholder="e.g. Counter Sales" className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:border-emerald-500 highlight-none" required />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Category <span className="text-red-500"> *</span></label>
                                <select required name="category" value={addMoneyForm.category} onChange={handleAddMoneyChange} className="w-full px-4 py-2 rounded-lg border border-gray-200 bg-white">
                                    <option value="" disabled className="hidden">Select Category</option>
                                    <option value="Income">General Income</option>
                                    <option value="Sales">Sales</option>
                                    <option value="Deposit">Deposit</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Deposit To <span className="text-red-500"> *</span></label>
                                <select required name="bankId" value={addMoneyForm.bankId} onChange={handleAddMoneyChange} className="w-full px-4 py-2 rounded-lg border border-gray-200 bg-white">
                                    <option value="" disabled className="hidden">Select Account</option>
                                    {bankList.map((bank) => (<option key={bank._id} value={bank._id}>{bank.accountName} - {bank.accountNumber && bank.accountNumber.slice(-4)}</option>))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Mode</label>
                                <select name="mode" value={addMoneyForm.mode} onChange={handleAddMoneyChange} className="w-full px-4 py-2 rounded-lg border border-gray-200 bg-white">
                                    <option value="Cash">Cash</option>
                                    <option value="UPI">UPI</option>
                                    <option value="Check">Check</option>
                                </select>
                            </div>

                            {(addMoneyForm.mode === "UPI" || addMoneyForm.mode === "Check") && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Reference No</label>
                                    <input type="text" name="reference" value={addMoneyForm.reference} onChange={handleAddMoneyChange} placeholder="Ref ID / Check No" className="w-full px-4 py-2 rounded-lg border border-gray-200" />
                                </div>
                            )}

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Note</label>
                                <textarea name="note" value={addMoneyForm.note} onChange={handleAddMoneyChange} rows="1" className="w-full px-4 py-2 rounded-lg border border-gray-200 resize-none" placeholder="Optional details..."></textarea>
                            </div>

                            <div className="flex items-center gap-2 pt-1 pb-1">
                                <input
                                    type="checkbox"
                                    id="isVikri"
                                    name="isVikri"
                                    checked={addMoneyForm.isVikri}
                                    onChange={handleAddMoneyChange}
                                    className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                                />
                                <label htmlFor="isVikri" className="text-sm font-medium text-gray-700 select-none cursor-pointer">
                                    Vikri Payment? (Add to Sales)
                                </label>
                            </div>

                            <button type="submit" className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl shadow-lg shadow-emerald-200 transition-all flex items-center justify-center gap-2 mt-2">
                                <Save size={20} /> Save Income
                            </button>
                        </form>
                    </div>
                </div>

                {/* --- 2. SPEND MONEY (EXPENSE) --- */}
                <div className="bg-white rounded-2xl shadow-sm border border-rose-100 overflow-hidden flex flex-col">
                    <div className="bg-rose-50/50 p-4 border-b border-rose-100 flex items-center gap-3">
                        <div className="p-2 bg-rose-100 text-rose-600 rounded-lg">
                            <ArrowUpCircle size={24} />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-gray-800">Record Expense</h2>
                            <p className="text-xs text-gray-500">Purchases, Bills, Withdrawals</p>
                        </div>
                    </div>

                    <div className="p-6">
                        <form onSubmit={handleSpendMoneySubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Amount (₹)<span className="text-red-500"> *</span></label>
                                <div className="relative">
                                    <span className="absolute left-3 top-2.5 text-gray-400 font-bold">₹</span>
                                    <input min={0} type="number" name="amount" value={spendMoneyForm.amount} onChange={handleSpendMoneyChange} placeholder="0.00" className="w-full pl-8 pr-4 py-2 rounded-lg border border-gray-200 focus:border-rose-500 focus:ring-2 focus:ring-rose-200 outline-none transition-all font-mono text-lg font-bold text-rose-600" required />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Particulars / Reason <span className="text-red-500"> *</span></label>
                                <input type="text" name="name" value={spendMoneyForm.name} onChange={handleSpendMoneyChange} placeholder="e.g. Electricity Bill" className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:border-rose-500" required />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Paid From <span className="text-red-500"> *</span></label>
                                <select required name="bankId" value={spendMoneyForm.bankId} onChange={handleSpendMoneyChange} className="w-full px-4 py-2 rounded-lg border border-gray-200 bg-white">
                                    <option value="" disabled className="hidden">Select Account</option>
                                    {bankList.map((bank) => (<option key={bank._id} value={bank._id}>{bank.accountName}</option>))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Mode</label>
                                <select name="mode" value={spendMoneyForm.mode} onChange={handleSpendMoneyChange} className="w-full px-4 py-2 rounded-lg border border-gray-200 bg-white">
                                    <option value="Cash">Cash</option>
                                    <option value="UPI">UPI</option>
                                    <option value="Check">Check</option>
                                </select>
                            </div>

                            {(spendMoneyForm.mode === "UPI" || spendMoneyForm.mode === "Check") && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Reference No</label>
                                    <input type="text" name="reference" value={spendMoneyForm.reference} onChange={handleSpendMoneyChange} className="w-full px-4 py-2 rounded-lg border border-gray-200" />
                                </div>
                            )}

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Note</label>
                                <textarea name="note" value={spendMoneyForm.note} onChange={handleSpendMoneyChange} rows="1" className="w-full px-4 py-2 rounded-lg border border-gray-200 resize-none" placeholder="Optional..."></textarea>
                            </div>

                            <button type="submit" className="w-full py-3 bg-rose-600 hover:bg-rose-700 text-white font-bold rounded-xl shadow-lg shadow-rose-200 transition-all flex items-center justify-center gap-2 mt-2">
                                <Save size={20} /> Save Expense
                            </button>
                        </form>
                    </div>
                </div>

                {/* --- 3. GIVE UDHAR (LENDING) --- */}
                <div className="bg-white rounded-2xl shadow-sm border border-amber-100 overflow-hidden flex flex-col">
                    <div className="bg-amber-50/50 p-4 border-b border-amber-100 flex items-center gap-3">
                        <div className="p-2 bg-amber-100 text-amber-600 rounded-lg">
                            <Briefcase size={24} />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-gray-800">Give Udhar</h2>
                            <p className="text-xs text-gray-500">Lend money (Outflow)</p>
                        </div>
                    </div>

                    <div className="p-6">
                        <form onSubmit={handleGiveUdharSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Total Bill Amount (₹)<span className="text-red-500"> *</span></label>
                                <div className="relative">
                                    <span className="absolute left-3 top-2.5 text-gray-400 font-bold">₹</span>
                                    <input min={0} type="number" name="amount" value={giveUdharForm.amount} onChange={handleGiveUdharChange} placeholder="0.00" className="w-full pl-8 pr-4 py-2 rounded-lg border border-gray-200 focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none transition-all font-mono text-lg font-bold text-amber-600" required />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Paid Amount (Inflow)</label>
                                    <div className="relative">
                                        <span className="absolute left-3 top-2.5 text-gray-400 font-bold">₹</span>
                                        <input min={0} type="number" name="paidAmount" value={giveUdharForm.paidAmount} onChange={handleGiveUdharChange} placeholder="0.00" className="w-full pl-8 pr-4 py-2 rounded-lg border border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all font-mono text-lg font-bold text-emerald-600" />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1 text-red-500">Remaining Udhar</label>
                                    <div className="relative">
                                        <span className="absolute left-3 top-2.5 text-gray-400 font-bold">₹</span>
                                        <input readOnly type="number" value={Math.max(0, (parseFloat(giveUdharForm.amount) || 0) - (parseFloat(giveUdharForm.paidAmount) || 0))} className="w-full pl-8 pr-4 py-2 rounded-lg border border-red-200 bg-red-50 focus:outline-none font-mono text-lg font-bold text-red-600" />
                                    </div>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Name <span className="text-red-500"> *</span></label>
                                <input type="text" name="name" value={giveUdharForm.name} onChange={handleGiveUdharChange} placeholder="Name" className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:border-amber-500" required />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Paid From (For Inflow) <span className="text-red-500"> *</span></label>
                                <select required name="bankId" value={giveUdharForm.bankId} onChange={handleGiveUdharChange} className="w-full px-4 py-2 rounded-lg border border-gray-200 bg-white">
                                    <option value="" disabled className="hidden">Select Account</option>
                                    {bankList.map((bank) => (<option key={bank._id} value={bank._id}>{bank.accountName}</option>))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Mode</label>
                                <select name="mode" value={giveUdharForm.mode} onChange={handleGiveUdharChange} className="w-full px-4 py-2 rounded-lg border border-gray-200 bg-white">
                                    <option value="Cash">Cash</option>
                                    <option value="UPI">UPI</option>
                                    <option value="Check">Check</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Note (Details)</label>
                                <textarea name="note" value={giveUdharForm.note} onChange={handleGiveUdharChange} rows="1" className="w-full px-4 py-2 rounded-lg border border-gray-200 resize-none" placeholder="Reason for udhar..."></textarea>
                            </div>

                            <button type="submit" className="w-full py-3 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-xl shadow-lg shadow-amber-200 transition-all flex items-center justify-center gap-2 mt-2">
                                <Save size={20} /> Record Transaction
                            </button>
                        </form>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default DailyTransaction;
